import useSupaSocket from '@/hooks/useSupaSocket';
import { PulseReport, GetPulseReportsResponse, featureService } from '@common/api/featureService';
import { supabase } from '@common/api/authService/supabase';
import dayjs from 'dayjs';
import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';

export type TimeFilter = 'All' | 'Today' | 'This Week' | 'This Month';
const DISABLE_WORKSPACE_PULSE_REPORT_API = true;

export interface PulseState {
    pulseData: PulseReport[];
    loading: boolean;
    activeFilter: TimeFilter;
    setPulseData: (data: PulseReport[]) => void;
    setLoading: (loading: boolean) => void;
    setActiveFilter: (filter: TimeFilter) => void;
    fetchPulseReports: (filter?: TimeFilter) => Promise<void>;
    clearPulseData: () => void;
}

export const pulseInitialState: PulseState = {
    pulseData: [],
    loading: false,
    activeFilter: 'Today',
    setPulseData: () => {},
    setLoading: () => {},
    setActiveFilter: () => {},
    fetchPulseReports: async () => {},
    clearPulseData: () => {}
};

// Helper function to get date range for time filters
const getDateRange = (filter: TimeFilter): { start_time?: string; end_time?: string } => {
    const today = dayjs().startOf('day');
    
    switch (filter) {
        case 'Today':
            return {
                start_time: today.toISOString(),
                end_time: today.add(1, 'day').toISOString()
            };
        case 'This Week':
            const weekStart = today.startOf('week');
            return {
                start_time: weekStart.toISOString(),
                end_time: today.add(1, 'day').toISOString()
            };
        case 'This Month':
            const monthStart = today.startOf('month');
            return {
                start_time: monthStart.toISOString(),
                end_time: today.add(1, 'day').toISOString()
            };
        case 'All':
        default:
            return {};
    }
};

export const createPulseProvider = (workspaceId: string | undefined): PulseState => {
    const [pulseData, setPulseData] = useState<PulseReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState<TimeFilter>('Today');


    const fetchPulseReports = useCallback(async (filter?: TimeFilter, refresh: boolean = true) => {
        const targetFilter = filter || activeFilter;
        try {
            if (refresh) {
                setLoading(true);
            }

            if (!workspaceId) {
                console.error('Workspace ID not found');
                setPulseData([]);
                return;
            }

            const dateRange = getDateRange(targetFilter);

            if (DISABLE_WORKSPACE_PULSE_REPORT_API) {
                // workspace_pulse_report API is being sunset. Keep provider logic but skip remote query.
                void dateRange;
                setPulseData([]);
                return;
            }

            // 构建查询
            let query = supabase
                .from('workspace_pulse_report')
                .select('*', { count: 'exact' })
                .eq('workspace_id', workspaceId)
                .order('created_at', { ascending: false });

            // 应用时间过滤
            if (dateRange.start_time) {
                query = query.gte('created_at', dateRange.start_time);
            }
            if (dateRange.end_time) {
                query = query.lt('created_at', dateRange.end_time);
            }

            // 应用分页
            const page = 1;
            const size = 999;
            const from = (page - 1) * size;
            const to = from + size - 1;

            query = query.range(from, to);

            // 执行查询
            const { data, error, count } = await query;

            if (error) {
                console.error('Failed to fetch pulse reports:', error);
                setPulseData([]);
                return;
            }

            // 映射数据到 PulseReport 格式
            const reports: PulseReport[] = (data || []).map((item: any) => ({
                id: item.id,
                user_id: item.workspace_id, // 使用 workspace_id 替代 user_id
                report_date: item.created_at, // 使用 created_at 替代 report_date
                report_type: item.report_type,
                title: item.title,
                brief: item.brief,
                img_url: item.img_url,
                oss_url: item.oss_url,
                status: item.status,
                created_at: item.created_at,
                updated_at: item.updated_at
            }));

            setPulseData(reports);
        } catch (err) {
            console.error('Failed to fetch pulse reports:', err);
            setPulseData([]);
        } finally {
            setLoading(false);
        }
    }, [activeFilter, workspaceId]);

    // 监听 activeFilter 和 workspaceId 变化，自动获取数据
    useEffect(() => {
        if (workspaceId) {
            fetchPulseReports(activeFilter);
        }
    }, [activeFilter, workspaceId, fetchPulseReports]);

    const clearPulseData = useCallback(() => {
        setPulseData([]);
    }, []);

    const callbacks = useMemo(()=>{
        return {
            onChange: (payload: any) => {
                fetchPulseReports(activeFilter, false);
            }
        }
    }, [activeFilter, fetchPulseReports]);
    
    useSupaSocket({
        table: 'user_pulse_report',
        enabled: true
    }, callbacks);
    
    return {
        pulseData,
        loading,
        activeFilter,
        setPulseData,
        setLoading,
        setActiveFilter,
        fetchPulseReports,
        clearPulseData
    };
};
